import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Handshake,
  Copy,
  Check,
  Loader2,
  DollarSign,
  Clock,
  BadgeCheck,
  AlertCircle,
  Share2,
  Send,
  Quote,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  fetchPartnerMe,
  acceptPartnerAgreement,
  setPartnerPayoutMethod,
  requestPartnerPayout,
  fetchTestimonialCandidates,
  setPartnerTestimonial,
  clearPartnerTestimonial,
} from "@/lib/api";
import UserBadge from "@/components/UserBadge";
import { PartnerTestimonialRibbon } from "@/components/PartnerTestimonialRibbon";

const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const SOURCE_LABEL = {
  sub: "Subscription",
  lulu: "Print book",
  ebook: "Ebook",
};

const STATUS_STYLE = {
  pending: { label: "Pending (gate not met)", color: "text-bone-500" },
  qualified: { label: "Qualified · unpaid", color: "text-terracotta" },
  paid: { label: "Paid", color: "text-emerald-400" },
  voided: { label: "Voided · refund", color: "text-red-400" },
};

export default function Partner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typedName, setTypedName] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState("");
  const [payoutHandle, setPayoutHandle] = useState("");
  const [savingMethod, setSavingMethod] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [candidates, setCandidates] = useState(null);       // Chapter-1 entries
  const [loadingCands, setLoadingCands] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickingId, setPickingId] = useState(null);
  const [publishing, setPublishing] = useState(false);
  // Preview stage — user hit "Preview this" on a candidate and we now show
  // them the exact ribbon that will appear on the landing page.
  const [previewEntry, setPreviewEntry] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const d = await fetchPartnerMe();
      setData(d);
      // Prefill saved payout method so the partner sees it, not blank.
      setPayoutMethod(d?.partner?.payout_method || "");
      setPayoutHandle(d?.partner?.payout_handle || "");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setData({ notPartner: true });
      } else {
        toast.error("Could not load partner dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.partner.link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed");
    }
  };

  // Native mobile share sheet — available on iOS Safari, Android Chrome, most
  // in-app browsers. Feature-detect once so desktop users don't see a button
  // that would silently fail.
  const canShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";
  const nativeShare = async () => {
    if (!canShare) return;
    try {
      await navigator.share({
        title: "Umbra · a voice shadow journal",
        text: "The quiet workbook I've been using — try it with my code:",
        url: data.partner.link,
      });
    } catch (e) {
      // User cancelled or share failed; only surface real errors.
      if (e && e.name !== "AbortError") {
        toast.error("Share failed");
      }
    }
  };

  const submitAcceptance = async () => {
    if (typedName.trim().length < 2) {
      toast.error("Please type your full legal name.");
      return;
    }
    try {
      setAccepting(true);
      await acceptPartnerAgreement({
        typed_name: typedName.trim(),
        version: data.agreement.version,
      });
      toast.success("Agreement accepted. Welcome to the circle.");
      await load();
    } catch (e) {
      toast.error("Could not record acceptance");
    } finally {
      setAccepting(false);
    }
  };

  const savePayoutMethod = async () => {
    const m = payoutMethod.trim();
    const h = payoutHandle.trim();
    if (!m || !h) {
      toast.error("Choose a method and add your handle first.");
      return;
    }
    try {
      setSavingMethod(true);
      await setPartnerPayoutMethod({ method: m, handle: h });
      toast.success("Payout details saved.");
      await load();
    } catch (e) {
      toast.error("Could not save payout method.");
    } finally {
      setSavingMethod(false);
    }
  };

  const submitPayoutRequest = async () => {
    try {
      setRequestingPayout(true);
      const r = await requestPartnerPayout();
      const amount = (r?.request?.amount_cents || 0) / 100;
      toast.success(`Payout requested — $${amount.toFixed(2)} queued for admin review.`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Backend sends structured object for below_threshold; fall back to string.
      if (detail && typeof detail === "object" && detail.reason === "below_threshold") {
        const need = (detail.threshold_cents / 100).toFixed(2);
        const have = (detail.total_cents / 100).toFixed(2);
        toast.error(`Below threshold — you have $${have} qualified, need $${need}.`);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Could not request payout.");
      }
    } finally {
      setRequestingPayout(false);
    }
  };

  const openTestimonialPicker = async () => {
    setPickerOpen(true);
    setPreviewEntry(null); // reset preview stage every open
    if (candidates) return; // already loaded once
    try {
      setLoadingCands(true);
      const r = await fetchTestimonialCandidates();
      setCandidates(r.entries || []);
    } catch {
      toast.error("Could not load your chapter 1 entries.");
      setPickerOpen(false);
    } finally {
      setLoadingCands(false);
    }
  };

  const publishTestimonial = async (entryId) => {
    try {
      setPublishing(true);
      setPickingId(entryId);
      await setPartnerTestimonial(entryId);
      toast.success("Testimonial published on your share page.");
      setPickerOpen(false);
      setPreviewEntry(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish.");
    } finally {
      setPublishing(false);
      setPickingId(null);
    }
  };

  const removeTestimonial = async () => {
    if (!window.confirm("Remove your public testimonial from the share page?")) return;
    try {
      setPublishing(true);
      await clearPartnerTestimonial();
      toast.success("Testimonial removed.");
      await load();
    } catch {
      toast.error("Could not remove testimonial.");
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-bone-400">
        <Loader2 className="h-5 w-5 animate-spin mr-3" /> loading
      </div>
    );
  }

  if (data?.notPartner) {
    return (
      <div className="min-h-screen bg-obsidian-900">
        <div className="max-w-2xl mx-auto px-6 md:px-12 pt-32 text-center">
          <Handshake className="h-8 w-8 text-terracotta mx-auto mb-6" />
          <h1 className="font-serif text-4xl text-bone-100 mb-4">
            Not yet a partner
          </h1>
          <p className="font-serif italic text-bone-400 mb-8">
            The Umbra partner program is invite-only. If you're a creator we've
            been in touch with, ask us to promote your account.
          </p>
          <Link
            to="/dashboard"
            data-testid="partner-back-dashboard"
            className="font-sans text-xs uppercase tracking-[0.24em] text-terracotta border-b border-terracotta pb-0.5"
          >
            ← Return to your workbook
          </Link>
        </div>
      </div>
    );
  }

  const { partner, agreement, totals = {}, recent_commissions = [], pending_payout_request } = data;
  const qualifiedCents = totals?.qualified?.cents || 0;
  const paidCents = totals?.paid?.cents || 0;
  const pendingCents = totals?.pending?.cents || 0;
  const thresholdCents = partner?.terms?.min_payout_threshold_cents ?? 2500;
  const hasSavedMethod = Boolean(partner?.payout_method && partner?.payout_handle);
  const canRequestPayout =
    hasSavedMethod && qualifiedCents >= thresholdCents && !pending_payout_request;
  const needsAcceptance =
    agreement && !agreement.accepted_at ? agreement : null;

  return (
    <div className="min-h-screen bg-obsidian-900">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100"
        >
          ← Workbook
        </Link>
        <UserBadge />
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-12 pb-24">
        <div className="flex items-center gap-3 mb-4">
          <Handshake className="h-5 w-5 text-terracotta" />
          <p
            className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta"
            data-testid="partner-eyebrow"
          >
            Partner · {partner.code}
          </p>
        </div>
        <h1
          className="font-serif text-4xl sm:text-5xl text-bone-100 mb-3"
          data-testid="partner-title"
        >
          Your partner circle
        </h1>
        <p className="font-serif italic text-bone-400 mb-12 max-w-2xl">
          The link below is yours. Everyone who signs up through it and pays
          contributes to your commission ledger.
        </p>

        {/* Link + code */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <div
            className="md:col-span-2 border border-obsidian-600 bg-obsidian-800/40 p-6"
            data-testid="partner-link-card"
          >
            <p className="font-sans text-[0.65rem] uppercase tracking-[0.28em] text-bone-500 mb-2">
              Your share link
            </p>
            <div className="flex items-center gap-2 sm:gap-3">
              <code className="flex-1 font-mono text-sm text-bone-100 truncate">
                {partner.link}
              </code>
              {canShare && (
                <Button
                  onClick={nativeShare}
                  data-testid="partner-share-link"
                  variant="outline"
                  className="rounded-none border-terracotta bg-terracotta/10 text-terracotta hover:text-obsidian-900 hover:bg-terracotta hover:border-terracotta font-sans uppercase tracking-wider text-xs px-3"
                >
                  <Share2 className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Share</span>
                </Button>
              )}
              <Button
                onClick={copyLink}
                data-testid="partner-copy-link"
                variant="outline"
                className="rounded-none border-bone-500 bg-transparent text-bone-100 hover:text-terracotta hover:border-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs px-3"
              >
                {copied ? (
                  <Check className="h-4 w-4 sm:mr-1" />
                ) : (
                  <Copy className="h-4 w-4 sm:mr-1" />
                )}
                <span className="hidden sm:inline">{copied ? "Copied" : "Copy"}</span>
              </Button>
            </div>
          </div>
          <div className="border border-obsidian-600 bg-obsidian-800/40 p-6">
            <p className="font-sans text-[0.65rem] uppercase tracking-[0.28em] text-bone-500 mb-2">
              Code
            </p>
            <p className="font-mono text-2xl text-terracotta mb-4" data-testid="partner-code">
              {partner.code}
            </p>
            <Link
              to={`/promo-card?ref=${encodeURIComponent(partner.code || "")}`}
              data-testid="partner-promo-card-link"
              className="inline-flex items-center gap-2 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-400 hover:text-terracotta transition-colors border-b border-bone-600 hover:border-terracotta pb-0.5"
            >
              Print promo card ↗
            </Link>
          </div>
        </div>

        {/* Money summary */}
        <div
          className="grid grid-cols-3 gap-3 sm:gap-4 mb-12"
          data-testid="partner-totals"
        >
          <div className="border border-terracotta bg-terracotta/5 p-4 sm:p-5">
            <p className="font-sans text-[0.55rem] sm:text-[0.65rem] uppercase tracking-[0.22em] sm:tracking-[0.28em] text-terracotta mb-2 flex items-center gap-1 sm:gap-2">
              <DollarSign className="h-3 w-3" /> Qualified
            </p>
            <p className="font-serif text-2xl sm:text-3xl text-bone-100" data-testid="partner-qualified">
              {dollars(qualifiedCents)}
            </p>
          </div>
          <div className="border border-obsidian-600 p-4 sm:p-5">
            <p className="font-sans text-[0.55rem] sm:text-[0.65rem] uppercase tracking-[0.22em] sm:tracking-[0.28em] text-bone-500 mb-2 flex items-center gap-1 sm:gap-2">
              <Clock className="h-3 w-3" /> Pending
            </p>
            <p className="font-serif text-2xl sm:text-3xl text-bone-300" data-testid="partner-pending">
              {dollars(pendingCents)}
            </p>
          </div>
          <div className="border border-obsidian-600 p-4 sm:p-5">
            <p className="font-sans text-[0.55rem] sm:text-[0.65rem] uppercase tracking-[0.22em] sm:tracking-[0.28em] text-bone-500 mb-2 flex items-center gap-1 sm:gap-2">
              <BadgeCheck className="h-3 w-3" /> Paid
            </p>
            <p className="font-serif text-2xl sm:text-3xl text-emerald-400" data-testid="partner-paid">
              {dollars(paidCents)}
            </p>
          </div>
        </div>

        {/* Payout portal — save method + self-serve request */}
        <div
          className="border border-obsidian-600 bg-obsidian-800/40 p-5 sm:p-6 mb-12"
          data-testid="payout-portal"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-1">
                Payout portal
              </p>
              <h2 className="font-serif text-2xl text-bone-100">
                Request your payout
              </h2>
              <p className="font-serif italic text-sm text-bone-400 mt-1">
                Threshold — {dollars(thresholdCents)} in qualified commissions.
              </p>
            </div>
            {pending_payout_request && (
              <span
                data-testid="payout-pending-badge"
                className="inline-flex items-center gap-2 border border-terracotta/40 text-terracotta font-sans uppercase tracking-[0.22em] text-[0.6rem] px-3 py-2"
              >
                <Clock className="h-3 w-3" /> Request pending review
              </span>
            )}
          </div>

          {pending_payout_request ? (
            <p
              className="font-sans text-sm text-bone-300"
              data-testid="payout-pending-info"
            >
              <span className="text-bone-100">
                {dollars(pending_payout_request.amount_cents)}
              </span>{" "}
              queued via <span className="text-bone-100">{pending_payout_request.method}</span> —{" "}
              handle <span className="font-mono text-bone-100">{pending_payout_request.handle}</span>.
              You'll get an email once the admin marks it paid.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-[180px,1fr] gap-3">
                <select
                  value={payoutMethod}
                  onChange={(e) => setPayoutMethod(e.target.value)}
                  data-testid="payout-method-select"
                  className="bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-3 font-sans text-base sm:text-sm focus:outline-none"
                >
                  <option value="">Method…</option>
                  <option value="venmo">Venmo</option>
                  <option value="paypal">PayPal</option>
                  <option value="zelle">Zelle</option>
                  <option value="cashapp">Cash App</option>
                  <option value="ach">ACH / Bank</option>
                  <option value="check">Check</option>
                  <option value="other">Other</option>
                </select>
                <input
                  type="text"
                  value={payoutHandle}
                  onChange={(e) => setPayoutHandle(e.target.value)}
                  data-testid="payout-handle-input"
                  placeholder="@your-venmo · paypal email · routing info"
                  className="bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-3 font-sans text-base sm:text-sm focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={savePayoutMethod}
                  disabled={savingMethod}
                  data-testid="payout-save-method-btn"
                  variant="outline"
                  className="rounded-none border-bone-500 bg-transparent text-bone-100 hover:text-terracotta hover:border-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px]"
                >
                  {savingMethod ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                  {hasSavedMethod ? "Update details" : "Save details"}
                </Button>
                <Button
                  onClick={submitPayoutRequest}
                  disabled={!canRequestPayout || requestingPayout}
                  data-testid="payout-request-btn"
                  className="rounded-none bg-terracotta hover:bg-terracotta-deep disabled:bg-obsidian-700 disabled:text-bone-500 text-bone-100 font-sans uppercase tracking-wider text-xs px-5 py-3 min-h-[44px]"
                >
                  {requestingPayout ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Request {dollars(qualifiedCents)}
                </Button>
              </div>
              {!hasSavedMethod && (
                <p className="font-sans text-xs text-bone-500" data-testid="payout-needs-method">
                  Save a payout method above before requesting.
                </p>
              )}
              {hasSavedMethod && qualifiedCents < thresholdCents && (
                <p className="font-sans text-xs text-bone-500" data-testid="payout-below-threshold">
                  Below the {dollars(thresholdCents)} threshold — earn{" "}
                  {dollars(thresholdCents - qualifiedCents)} more to unlock the button.
                </p>
              )}
            </div>
          )}
        </div>

        {/* First Prompt Widget — public testimonial from chapter 1 */}
        <div
          className="border border-obsidian-600 bg-obsidian-800/40 p-5 sm:p-6 mb-12"
          data-testid="testimonial-widget"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-1">
                First Prompt Widget
              </p>
              <h2 className="font-serif text-2xl text-bone-100">
                Your public testimonial
              </h2>
              <p className="font-serif italic text-sm text-bone-400 mt-1 max-w-xl">
                Pick one Chapter 1 answer to show every visitor who lands on{" "}
                <code className="text-bone-200 break-all">/?ref={partner.code}</code>. It makes the
                share feel like a note from you, not a promo.
              </p>
            </div>
          </div>

          {partner.testimonial ? (
            <div data-testid="testimonial-current">
              <div className="border border-obsidian-600 bg-obsidian-900 p-5 mb-4 relative">
                <Quote className="h-5 w-5 text-terracotta absolute top-4 left-4 opacity-60" />
                <p className="font-sans uppercase tracking-[0.22em] text-[0.55rem] text-bone-500 mb-2 pl-8">
                  Chapter 1 · {partner.testimonial.prompt.slice(0, 80)}
                </p>
                <blockquote
                  className="font-serif italic text-lg text-bone-100 leading-relaxed pl-8"
                  data-testid="testimonial-quote"
                >
                  {partner.testimonial.quote}
                </blockquote>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={openTestimonialPicker}
                  disabled={publishing}
                  data-testid="testimonial-change-btn"
                  variant="outline"
                  className="rounded-none border-bone-500 bg-transparent text-bone-100 hover:text-terracotta hover:border-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px]"
                >
                  Change quote
                </Button>
                <Button
                  onClick={removeTestimonial}
                  disabled={publishing}
                  data-testid="testimonial-remove-btn"
                  variant="outline"
                  className="rounded-none border-obsidian-600 bg-transparent text-bone-500 hover:text-red-400 hover:border-red-400 hover:bg-transparent font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px]"
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={openTestimonialPicker}
                data-testid="testimonial-add-btn"
                className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-5 py-3 min-h-[44px]"
              >
                <Quote className="h-4 w-4 mr-2" /> Pick an answer to feature
              </Button>
              <p className="font-sans text-xs text-bone-500">
                Only Chapter 1 · your answer stays private until you publish.
              </p>
            </div>
          )}
        </div>

        {/* Agreement acceptance banner */}
        {needsAcceptance && (
          <div
            className="border border-terracotta bg-terracotta/5 p-6 mb-12"
            data-testid="partner-agreement-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-4 w-4 text-terracotta" />
              <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta">
                Agreement pending your acceptance
              </p>
            </div>
            <h2 className="font-serif text-2xl text-bone-100 mb-3">
              Please review and accept your partner agreement.
            </h2>
            <div
              className="max-h-64 overflow-y-auto border border-obsidian-600 bg-obsidian-900/60 p-5 mb-5 font-sans text-sm text-bone-300 leading-relaxed prose-invert"
              data-testid="partner-agreement-html"
              dangerouslySetInnerHTML={{ __html: needsAcceptance.html }}
            />
            <label className="block font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mb-2">
              Type your full legal name to accept
            </label>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              data-testid="partner-typed-name"
              placeholder="Alex Rivera"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-4 py-3 font-sans text-base mb-4 focus:outline-none"
            />
            <Button
              onClick={submitAcceptance}
              disabled={accepting || typedName.trim().length < 2}
              data-testid="partner-accept-btn"
              className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-5 px-8"
            >
              {accepting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              I Accept
            </Button>
          </div>
        )}

        {/* Terms overview */}
        <div className="border border-obsidian-600 p-6 mb-12">
          <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-bone-500 mb-4">
            Your rates
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-center">
            {[
              ["Sub cycle 1", partner.terms.sub_cycle_1_pct + "%"],
              ["Sub cycle 2", partner.terms.sub_cycle_2_pct + "%"],
              ["Sub cycle 3+", partner.terms.sub_cycle_3plus_pct + "%"],
              ["Lulu profit", partner.terms.lulu_profit_pct + "%"],
              ["Ebook", partner.terms.ebook_pct + "%"],
            ].map(([label, val]) => (
              <div key={label}>
                <p className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-bone-500 mb-1">
                  {label}
                </p>
                <p className="font-serif text-xl text-bone-100">{val}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 font-sans text-xs text-bone-500 leading-relaxed">
            Monthly qualification threshold —{" "}
            <span className="text-bone-200">
              {partner.terms.monthly_sub_minimum}
            </span>{" "}
            new first-billing subscribers must sign up in a calendar month
            before that month's subscription commissions unlock.
          </p>
        </div>

        {/* Recent commissions — desktop table + mobile card stack */}
        <div data-testid="partner-commissions-table">
          {/* Desktop table (md+) */}
          <div className="hidden md:block border border-obsidian-600">
            <div className="grid grid-cols-6 gap-4 px-5 py-3 border-b border-obsidian-600 font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500">
              <div>When</div>
              <div>Source</div>
              <div>Cycle</div>
              <div className="text-right">Gross</div>
              <div className="text-right">Rate · $</div>
              <div className="text-right">Status</div>
            </div>
            {recent_commissions.length === 0 && (
              <p className="font-sans text-bone-500 text-sm p-6">
                No commissions yet. Share your link — the first paid signup
                starts the ledger.
              </p>
            )}
            {recent_commissions.map((c) => {
              const s = STATUS_STYLE[c.status] || { label: c.status, color: "text-bone-400" };
              return (
                <div
                  key={c.id}
                  className="grid grid-cols-6 gap-4 px-5 py-4 border-b border-obsidian-600 last:border-b-0 items-center"
                  data-testid={`partner-commission-${c.id}`}
                >
                  <div className="font-sans text-xs text-bone-400">
                    {new Date(c.created_at).toLocaleDateString()}
                  </div>
                  <div className="font-sans text-sm text-bone-200">
                    {SOURCE_LABEL[c.source_type] || c.source_type}
                  </div>
                  <div className="font-sans text-sm text-bone-400">
                    {c.billing_cycle ? `#${c.billing_cycle}` : "—"}
                  </div>
                  <div className="text-right font-sans text-sm text-bone-300">
                    {dollars(c.gross_cents)}
                  </div>
                  <div className="text-right font-sans text-sm text-bone-100">
                    {c.rate_pct}% · {dollars(c.commission_cents)}
                  </div>
                  <div className={`text-right font-sans text-xs ${s.color}`}>
                    {s.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile card stack (< md) */}
          <div className="md:hidden space-y-3">
            {recent_commissions.length === 0 && (
              <p className="font-sans text-bone-500 text-sm p-5 border border-obsidian-600">
                No commissions yet. Share your link — the first paid signup
                starts the ledger.
              </p>
            )}
            {recent_commissions.map((c) => {
              const s = STATUS_STYLE[c.status] || { label: c.status, color: "text-bone-400" };
              return (
                <div
                  key={c.id}
                  data-testid={`partner-commission-${c.id}-mobile`}
                  className="border border-obsidian-600 bg-obsidian-800/30 p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-serif text-lg text-bone-100">
                        {dollars(c.commission_cents)}
                      </p>
                      <p className="font-sans text-[0.65rem] uppercase tracking-[0.22em] text-bone-500 mt-0.5">
                        {SOURCE_LABEL[c.source_type] || c.source_type}
                        {c.billing_cycle ? ` · cycle #${c.billing_cycle}` : ""}
                        {" · "}
                        {c.rate_pct}%
                      </p>
                    </div>
                    <span className={`font-sans text-[0.6rem] uppercase tracking-[0.22em] ${s.color}`}>
                      {s.label}
                    </span>
                  </div>
                  <div className="flex items-center justify-between font-sans text-xs text-bone-400 pt-2 border-t border-obsidian-600/60">
                    <span>{new Date(c.created_at).toLocaleDateString()}</span>
                    <span>on {dollars(c.gross_cents)} gross</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Testimonial picker modal */}
      {pickerOpen && (
        <div
          data-testid="testimonial-picker-modal"
          className="fixed inset-0 z-50 bg-obsidian-900/85 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
          onClick={() => !publishing && setPickerOpen(false)}
        >
          <div
            className="w-full max-w-2xl bg-obsidian-800 border border-obsidian-600 my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 border-b border-obsidian-600 p-5">
              <div className="min-w-0">
                <p className="font-sans uppercase tracking-[0.2em] text-[0.6rem] text-terracotta mb-1">
                  {previewEntry ? "Preview on landing page" : "Pick a Chapter 1 answer"}
                </p>
                <p className="font-serif text-lg text-bone-100">
                  {previewEntry
                    ? "This is exactly what a visitor sees."
                    : "Which one would you want a stranger to read first?"}
                </p>
              </div>
              <button
                onClick={() => !publishing && setPickerOpen(false)}
                data-testid="testimonial-picker-close"
                className="font-sans uppercase tracking-[0.2em] text-xs text-bone-400 hover:text-terracotta transition-colors min-h-[44px] px-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {previewEntry ? (
              /* ── Stage 2 · Preview ──────────────────────────────────── */
              <div
                className="p-4 sm:p-6 space-y-5"
                data-testid="testimonial-preview-stage"
              >
                <p className="font-sans text-xs text-bone-500">
                  Visitors landing on{" "}
                  <code className="text-bone-300">/?ref={partner.code}</code>{" "}
                  will see this above the sign-in button:
                </p>
                <div className="bg-obsidian-900 p-4 sm:p-5 border border-obsidian-600">
                  <PartnerTestimonialRibbon
                    name={data?.partner?.name || "you"}
                    quote={(previewEntry.text || "").trim().slice(0, 600)}
                    variant="preview"
                  />
                </div>
                <p className="font-sans italic text-xs text-bone-400 leading-relaxed">
                  Once you publish, the quote is snapshotted — future edits to
                  your journal entry won't change what strangers see here until
                  you republish.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <Button
                    onClick={() => setPreviewEntry(null)}
                    disabled={publishing}
                    data-testid="testimonial-preview-back"
                    variant="outline"
                    className="rounded-none border-bone-500 bg-transparent text-bone-100 hover:text-terracotta hover:border-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px]"
                  >
                    ← Pick different
                  </Button>
                  <Button
                    onClick={() => publishTestimonial(previewEntry.id)}
                    disabled={publishing}
                    data-testid="testimonial-preview-publish"
                    className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-5 py-3 min-h-[44px]"
                  >
                    {publishing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Publish this
                  </Button>
                </div>
              </div>
            ) : (
              /* ── Stage 1 · Candidate list ──────────────────────────── */
              <div className="p-4 sm:p-6 max-h-[70vh] overflow-y-auto space-y-3">
                {loadingCands ? (
                  <div className="flex items-center gap-3 text-bone-400 py-8">
                    <Loader2 className="h-4 w-4 animate-spin" /> loading your entries
                  </div>
                ) : !candidates || candidates.length === 0 ? (
                  <p
                    className="font-sans text-sm text-bone-500 py-8 text-center"
                    data-testid="testimonial-picker-empty"
                  >
                    You haven't answered a Chapter 1 prompt yet — write one first, then come back.
                  </p>
                ) : (
                  candidates.map((e) => {
                    const text = (e.text || "").trim();
                    const tooShort = text.length < 40;
                    return (
                      <div
                        key={e.id}
                        data-testid={`testimonial-candidate-${e.id}`}
                        className="border border-obsidian-600 bg-obsidian-900 p-4"
                      >
                        <p className="font-sans uppercase tracking-[0.22em] text-[0.55rem] text-bone-500 mb-2">
                          {e.prompt?.slice(0, 100)}
                        </p>
                        <p className="font-serif italic text-base text-bone-200 leading-relaxed mb-3 line-clamp-4">
                          {text || "(empty)"}
                        </p>
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-sans text-[0.6rem] uppercase tracking-[0.2em] text-bone-500">
                            {text.length} chars {tooShort && "· too short"}
                          </span>
                          <Button
                            onClick={() => setPreviewEntry(e)}
                            disabled={tooShort}
                            data-testid={`testimonial-preview-${e.id}`}
                            className="rounded-none bg-terracotta hover:bg-terracotta-deep disabled:bg-obsidian-700 disabled:text-bone-500 text-bone-100 font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px]"
                          >
                            Preview this →
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
